import React, { useRef, useEffect, ElementType, ReactNode } from 'react';
import { gsap, EASE, prefersReducedMotion } from './gsap';

interface RevealProps {
    children: ReactNode;
    as?: ElementType;
    className?: string;
    /** Distance travelled on entry, in px. */
    y?: number;
    delay?: number;
    /**
     * When set, direct children are staggered instead of the wrapper moving as
     * one block — used for the hairline rows and feature grids.
     */
    stagger?: number;
    start?: string;
    style?: React.CSSProperties;
}

/**
 * Generic scroll entrance: a short rise plus fade on power2.out, which is the
 * reference's default for everything that isn't a headline.
 */
const Reveal: React.FC<RevealProps> = ({
    children,
    as: Tag = 'div',
    className = '',
    y = 28,
    delay = 0,
    stagger,
    start = 'top 88%',
    style,
}) => {
    const ref = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el || prefersReducedMotion()) return;

        const ctx = gsap.context(() => {
            const targets = stagger ? Array.from(el.children) : el;
            gsap.from(targets, {
                y,
                opacity: 0,
                duration: 0.85,
                ease: EASE.out,
                delay,
                stagger: stagger ?? 0,
                scrollTrigger: { trigger: el, start, once: true },
            });
        }, el);

        return () => ctx.revert();
    }, [y, delay, stagger, start]);

    return (
        <Tag ref={ref} className={className} style={style}>
            {children}
        </Tag>
    );
};

export default Reveal;
